import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format, parse } from "date-fns";
import { Eye, Plus, Receipt } from "lucide-react";
import { axiosInstance } from "@/pages/auth/auth.context";
import { BonusDTO } from "@/types/bonus";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { BonusTypePill } from "@/components/admin/bonuses/BonusTypePill";
import { StatusPill } from "@/components/brand/StatusPill";

export default function ManageBonuses() {
  const [bonusPrograms, setBonusPrograms] = useState<BonusDTO[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);

  useEffect(() => {
    async function fetchBonuses() {
      try {
        const params = includeArchived ? "?includeArchived=true" : "";
        const response = await axiosInstance.get(`bonus${params}`);
        const data = response.data;
        setBonusPrograms(Array.isArray(data) ? data : (data.content ?? []));
      } catch (err) {
        setError("Failed to fetch bonus programs");
        setBonusPrograms([]);
      } finally {
        setLoading(false);
      }
    }

    fetchBonuses();
  }, [includeArchived]);

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Wraps: "Generate Bonus Checks" and "Create New Bonus Program" are
          together about 470px of unbreakable label, so beside a title they
          pushed the page wider than a phone. */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-3xl font-bold">Bonus Programs</h1>
        <div className="flex flex-wrap gap-2">
          <Link to="/admin/check-batches/create-bonus">
            <Button variant="outline">
              <Receipt className="mr-2 h-4 w-4" />
              Generate Bonus Checks
            </Button>
          </Link>
          <Link to="/admin/bonuses/create">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Create New Bonus Program
            </Button>
          </Link>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id="includeArchived"
          checked={includeArchived}
          onCheckedChange={setIncludeArchived}
        />
        <Label
          htmlFor="includeArchived"
          className="text-sm text-muted-foreground cursor-pointer"
        >
          Include archived
        </Label>
      </div>

      {error && <p className="text-destructive">{error}</p>}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Program Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Start Date</TableHead>
              <TableHead>End Date</TableHead>
              <TableHead>Req. Parties</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center text-muted-foreground"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : bonusPrograms.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center text-muted-foreground"
                >
                  No bonus programs found.
                </TableCell>
              </TableRow>
            ) : (
              bonusPrograms.map((program) => (
                <TableRow key={program.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {program.title}
                      {program.archived && (
                        <StatusPill tone="warning">Archived</StatusPill>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <BonusTypePill type={program.bonusType} />
                  </TableCell>
                  <TableCell>
                    {format(
                      parse(program.startDate, "yyyy-MM-dd", new Date()),
                      "MMM d, yyyy",
                    )}
                  </TableCell>
                  <TableCell>
                    {format(
                      parse(program.endDate, "yyyy-MM-dd", new Date()),
                      "MMM d, yyyy",
                    )}
                  </TableCell>
                  <TableCell>{program.requiredParties ?? "—"}</TableCell>
                  <TableCell>
                    {program.dollarAmount != null
                      ? `$${program.dollarAmount}`
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <Link to={`/admin/bonuses/${program.id}`}>
                      <Button variant="outline" size="sm">
                        <Eye className="mr-2 h-4 w-4" />
                        View Details
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
